import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import {
    Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import {
    ShieldCheck, Users as UsersIcon, Gavel, Flag, Scale, Banknote, Search,
    Ban, CheckCircle2, Trash2, RotateCcw, TrendingUp, AlertTriangle, Lock,
    Package, Activity as ActivityIcon, Trophy, UserPlus, ShoppingBag, Clock, Crown,
    Download, Mail, Store, Send, Loader2,
    BarChart3, Target, ExternalLink, Lightbulb, Swords, XCircle, Minus, Sparkles,
} from "lucide-react";

const cls = (...a) => a.filter(Boolean).join(" ");

const TONE = {
    OPEN: "bg-accent text-accent-foreground border-accent/50",
    PAID: "bg-success/10 text-success border-success/30",
    VERIFIED: "bg-success/10 text-success border-success/30",
    READY: "bg-success/10 text-success border-success/30",
    RESOLVED: "bg-success/10 text-success border-success/30",
    PENDING: "bg-warning/10 text-warning border-warning/30",
    PROCESSING: "bg-warning/10 text-warning border-warning/30",
    UNDER_REVIEW: "bg-warning/10 text-warning border-warning/30",
    RECEIVED: "bg-warning/10 text-warning border-warning/30",
    REMOVED: "bg-destructive/10 text-destructive border-destructive/30",
    CANCELLED: "bg-destructive/10 text-destructive border-destructive/30",
    DISMISSED: "bg-secondary text-secondary-foreground border-border",
    SUSPENDED: "bg-destructive/10 text-destructive border-destructive/30",
};

function Pill({ value, className }) {
    return (
        <span
            data-testid="admin-status-pill"
            className={cls(
                "inline-flex items-center rounded-full border px-2.5 py-0.5 text-[11px] font-medium",
                TONE[value] || "bg-secondary text-secondary-foreground border-border",
                className,
            )}
        >
            {String(value || "").replace(/_/g, " ")}
        </span>
    );
}

// ----------------- Overview -----------------
function StatCard({ icon: Icon, label, value, accent, testid }) {
    return (
        <Card data-testid={testid} className="p-5">
            <div className="flex items-center justify-between">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{label}</p>
                <Icon className={cls("h-4 w-4", accent || "text-electric")} aria-hidden />
            </div>
            <p className="mt-2 font-display text-2xl font-bold tabular-nums">{value}</p>
        </Card>
    );
}

function EmailDeliverabilityCard() {
    const [to, setTo] = useState("");
    const [sending, setSending] = useState(false);
    const [result, setResult] = useState(null);
    const [status, setStatus] = useState(null);
    useEffect(() => { api.get("/admin/email/status").then(({ data }) => setStatus(data)).catch(() => {}); }, []);

    const send = async (e) => {
        e?.preventDefault?.();
        setSending(true); setResult(null);
        try {
            const { data } = await api.post("/admin/email/test", { to });
            setResult(data);
            if (data.ok) toast.success("Test email accepted by SendGrid"); else toast.error(data.detail || "Send failed");
        } catch (err) {
            const d = err?.response?.data?.detail || "Send failed";
            setResult({ ok: false, detail: d }); toast.error(d);
        } finally { setSending(false); }
    };

    return (
        <Card className="p-5" data-testid="admin-email-deliverability">
            <div className="flex items-start gap-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-electric/10 text-electric"><Mail className="h-5 w-5" /></div>
                <div className="flex-1">
                    <p className="font-display font-semibold">Email deliverability</p>
                    <p className="text-sm text-muted-foreground">
                        Sending as{" "}
                        <span className="font-medium text-foreground">{status?.sender_email || "…"}</span>
                        {status && (
                            <span className={`ml-2 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs ${status.email_enabled ? "bg-success/15 text-success" : "bg-warning/15 text-warning"}`}>
                                {status.email_enabled ? <CheckCircle2 className="h-3 w-3" /> : <AlertTriangle className="h-3 w-3" />}
                                {status.email_enabled ? "Enabled" : "Disabled"}
                            </span>
                        )}
                    </p>
                </div>
            </div>
            <form onSubmit={send} className="mt-4 flex flex-col gap-2 sm:flex-row">
                <Input
                    type="email" value={to} onChange={(e) => setTo(e.target.value)} required
                    placeholder="you@example.com" className="sm:max-w-xs" data-testid="email-test-input"
                />
                <Button type="submit" disabled={sending || !to} className="rounded-[10px]" data-testid="email-test-send">
                    {sending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
                    Send test email
                </Button>
            </form>
            {result && (
                <div className={`mt-3 flex items-start gap-2 rounded-lg border px-3 py-2 text-xs ${result.ok ? "border-success/30 bg-success/10 text-success" : "border-warning/30 bg-warning/10 text-warning"}`} data-testid="email-test-result">
                    {result.ok ? <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" /> : <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />}
                    <span>{result.detail}{result.status_code ? ` (SendGrid ${result.status_code})` : ""}</span>
                </div>
            )}
        </Card>
    );
}

function Overview({ stats }) {
    if (!stats) return <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">{Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-28 rounded-xl" />)}</div>;
    const ac = stats.auction_counts || {};
    return (
        <div className="space-y-6">
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <StatCard testid="stat-users" icon={UsersIcon} label="Total users" value={stats.users_total} />
                <StatCard testid="stat-open" icon={Gavel} label="Open auctions" value={stats.auctions_open} />
                <StatCard testid="stat-bids" icon={Lock} label="Sealed bids" value={stats.bids_total} />
                <StatCard testid="stat-gmv" icon={TrendingUp} label="GMV (paid)" value={money(stats.gmv_paid)} accent="text-brass" />
                <StatCard testid="stat-disputes" icon={Scale} label="Open disputes" value={stats.disputes_open} accent={stats.disputes_open ? "text-warning" : "text-electric"} />
                <StatCard testid="stat-reports" icon={Flag} label="Pending reports" value={stats.reports_pending} accent={stats.reports_pending ? "text-warning" : "text-electric"} />
                <StatCard testid="stat-payouts" icon={Banknote} label="Pending payouts" value={`${stats.payouts_pending} · ${money(stats.payouts_pending_total)}`} accent="text-brass" />
                <StatCard testid="stat-suspended" icon={Ban} label="Suspended users" value={stats.users_suspended} accent={stats.users_suspended ? "text-destructive" : "text-electric"} />
            </div>
            <Card className="p-5">
                <p className="mb-3 font-display font-semibold">Auctions by status</p>
                <div className="flex flex-wrap gap-2">
                    {Object.entries(ac).map(([k, v]) => (
                        <span key={k} className="inline-flex items-center gap-2 rounded-lg border border-border bg-secondary/50 px-3 py-1.5 text-sm">
                            <Pill value={k} />
                            <span className="font-display font-semibold tabular-nums">{v}</span>
                        </span>
                    ))}
                </div>
            </Card>
            <EmailDeliverabilityCard />
        </div>
    );
}

// ----------------- Users -----------------
const SELLER_STATUSES = ["NOT_STARTED", "PENDING", "VERIFIED", "REJECTED"];

function UsersTab() {
    const [rows, setRows] = useState(null);
    const [q, setQ] = useState("");
    const [counts, setCounts] = useState(null);
    const [verifiedOnly, setVerifiedOnly] = useState(false);
    const [exporting, setExporting] = useState("");
    const load = useCallback((query = "") => {
        api.get("/admin/users", { params: query ? { q: query } : {} })
            .then(({ data }) => setRows(data))
            .catch(() => { setRows([]); toast.error("Failed to load users"); });
    }, []);
    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        api.get("/admin/users/audience-counts").then(({ data }) => setCounts(data)).catch(() => {});
    }, []);

    const exportCsv = async (group) => {
        setExporting(group);
        try {
            const res = await api.get("/admin/users/export", {
                params: { group, verified_only: verifiedOnly },
                responseType: "blob",
            });
            const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
            const a = document.createElement("a");
            a.href = url;
            a.download = `omniauc-users-${group}${verifiedOnly ? "-verified" : ""}.csv`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            toast.success(`Exported ${group} list — import it into Mailchimp/Brevo`);
        } catch (e) {
            toast.error("Export failed");
        } finally {
            setExporting("");
        }
    };

    const toggleSuspend = async (u) => {
        try {
            await api.post(`/admin/users/${u.id}/suspend`, { suspended: !u.suspended });
            toast.success(u.suspended ? "User reinstated" : "User suspended");
            load(q);
        } catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
    };
    const setSeller = async (u, status) => {
        try {
            await api.post(`/admin/users/${u.id}/seller-status`, { status });
            toast.success(`Seller status set to ${status}`);
            load(q);
        } catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
    };

    return (
        <div className="space-y-4">
            <Card className="p-5" data-testid="admin-email-export-card">
                <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="flex items-start gap-3">
                        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-electric/10 text-electric"><Mail className="h-5 w-5" /></div>
                        <div>
                            <h3 className="font-display font-semibold">Export email list</h3>
                            <p className="text-sm text-muted-foreground">Download your registered users as a CSV to import into Mailchimp, Brevo, or any email tool.</p>
                        </div>
                    </div>
                    <label className="flex items-center gap-2 rounded-lg border border-border bg-secondary/30 px-3 py-2 text-sm" htmlFor="export-verified-only">
                        <Switch id="export-verified-only" checked={verifiedOnly} onCheckedChange={setVerifiedOnly} data-testid="export-verified-toggle" />
                        <span>Verified emails only</span>
                    </label>
                </div>
                <div className="mt-4 grid gap-3 sm:grid-cols-3">
                    {[
                        { key: "all", label: "All users", icon: UsersIcon },
                        { key: "sellers", label: "Sellers", icon: Store },
                        { key: "buyers", label: "Buyers", icon: ShoppingBag },
                    ].map(({ key, label, icon: Icon }) => {
                        const c = counts?.[key];
                        const n = c ? (verifiedOnly ? c.verified : c.total) : null;
                        return (
                            <div key={key} className="flex items-center justify-between rounded-lg border border-border p-3">
                                <div className="flex items-center gap-2">
                                    <Icon className="h-4 w-4 text-muted-foreground" />
                                    <div>
                                        <div className="text-sm font-medium">{label}</div>
                                        <div className="text-xs text-muted-foreground" data-testid={`export-count-${key}`}>
                                            {n === null ? "…" : `${n} recipient${n === 1 ? "" : "s"}`}
                                        </div>
                                    </div>
                                </div>
                                <Button
                                    size="sm"
                                    variant="secondary"
                                    className="rounded-[10px]"
                                    disabled={exporting === key || n === 0}
                                    onClick={() => exportCsv(key)}
                                    data-testid={`export-${key}-btn`}
                                >
                                    <Download className="mr-1.5 h-3.5 w-3.5" />
                                    {exporting === key ? "…" : "CSV"}
                                </Button>
                            </div>
                        );
                    })}
                </div>
                <p className="mt-3 text-xs text-muted-foreground">
                    Tip: only email people who expect to hear from you. Sending to unverified or cold addresses can hurt deliverability and may breach anti-spam laws.
                </p>
            </Card>

            <form onSubmit={(e) => { e.preventDefault(); load(q); }} className="flex gap-2">
                <div className="relative flex-1 max-w-sm">
                    <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input data-testid="admin-user-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or email" className="pl-9" />
                </div>
                <Button type="submit" variant="secondary" className="rounded-[10px]" data-testid="admin-user-search-btn">Search</Button>
            </form>
            {rows === null ? <Skeleton className="h-72 w-full rounded-xl" /> : (
                <Card className="overflow-hidden">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>User</TableHead>
                                <TableHead>Role</TableHead>
                                <TableHead>Seller status</TableHead>
                                <TableHead>Account</TableHead>
                                <TableHead className="text-right">Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {rows.map((u) => (
                                <TableRow key={u.id} data-testid="admin-user-row">
                                    <TableCell>
                                        <div className="font-medium">{u.name} {u.is_admin && <span className="ml-1 rounded bg-electric/15 px-1.5 py-0.5 text-[10px] text-electric">ADMIN</span>}</div>
                                        <div className="text-xs text-muted-foreground">{u.email}</div>
                                    </TableCell>
                                    <TableCell className="capitalize text-sm">{u.role}</TableCell>
                                    <TableCell>
                                        <Select value={u.seller_status} onValueChange={(v) => setSeller(u, v)} disabled={u.is_admin}>
                                            <SelectTrigger className="h-8 w-[150px]" data-testid="admin-seller-status-select"><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                {SELLER_STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}
                                            </SelectContent>
                                        </Select>
                                    </TableCell>
                                    <TableCell>{u.suspended ? <Pill value="SUSPENDED" /> : <span className="text-xs text-muted-foreground">Active</span>}</TableCell>
                                    <TableCell className="text-right">
                                        <Button
                                            size="sm"
                                            variant={u.suspended ? "secondary" : "destructive"}
                                            className="rounded-[10px]"
                                            disabled={u.is_admin}
                                            onClick={() => toggleSuspend(u)}
                                            data-testid="admin-suspend-btn"
                                        >
                                            {u.suspended ? <><CheckCircle2 className="mr-1.5 h-3.5 w-3.5" />Reinstate</> : <><Ban className="mr-1.5 h-3.5 w-3.5" />Suspend</>}
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Card>
            )}
        </div>
    );
}

// ----------------- Listings -----------------
const AUCTION_FILTERS = ["ALL", "OPEN", "CLOSED_TIME", "CLOSED_MAX_BIDDERS", "PAID", "REMOVED"];

function ListingsTab() {
    const [rows, setRows] = useState(null);
    const [status, setStatus] = useState("ALL");
    const [removeTarget, setRemoveTarget] = useState(null);
    const [reason, setReason] = useState("");

    const load = useCallback((st = "ALL") => {
        api.get("/admin/auctions", { params: st !== "ALL" ? { status: st } : {} })
            .then(({ data }) => setRows(data))
            .catch(() => { setRows([]); toast.error("Failed to load listings"); });
    }, []);
    useEffect(() => { load(status); }, [status, load]);

    const doRemove = async () => {
        try {
            await api.post(`/admin/auctions/${removeTarget.id}/remove`, { removed: true, reason });
            toast.success("Listing removed");
            setRemoveTarget(null); setReason(""); load(status);
        } catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
    };
    const restore = async (a) => {
        try {
            await api.post(`/admin/auctions/${a.id}/remove`, { removed: false });
            toast.success("Listing restored");
            load(status);
        } catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
    };

    return (
        <div className="space-y-4">
            <div className="flex items-center gap-2">
                <Select value={status} onValueChange={setStatus}>
                    <SelectTrigger className="w-[200px]" data-testid="admin-listing-filter"><SelectValue /></SelectTrigger>
                    <SelectContent>{AUCTION_FILTERS.map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
                </Select>
            </div>
            {rows === null ? <Skeleton className="h-72 w-full rounded-xl" /> : (
                <Card className="overflow-hidden">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Listing</TableHead>
                                <TableHead>Seller</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead className="text-right">Bidders</TableHead>
                                <TableHead className="text-right">Price</TableHead>
                                <TableHead className="text-right">Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {rows.map((a) => (
                                <TableRow key={a.id} data-testid="admin-listing-row">
                                    <TableCell className="max-w-[260px]">
                                        <div className="truncate font-medium">{a.title}</div>
                                        <div className="text-xs text-muted-foreground">{a.category}</div>
                                    </TableCell>
                                    <TableCell className="text-sm">{a.seller_name}</TableCell>
                                    <TableCell><Pill value={a.status} /></TableCell>
                                    <TableCell className="text-right tabular-nums">{a.unique_bidder_count}/{a.max_bidders}</TableCell>
                                    <TableCell className="text-right tabular-nums">{money(a.winning_amount ?? a.starting_price)}</TableCell>
                                    <TableCell className="text-right">
                                        {a.status === "REMOVED" ? (
                                            <Button size="sm" variant="secondary" className="rounded-[10px]" onClick={() => restore(a)} data-testid="admin-restore-btn"><RotateCcw className="mr-1.5 h-3.5 w-3.5" />Restore</Button>
                                        ) : (
                                            <Button size="sm" variant="destructive" className="rounded-[10px]" onClick={() => setRemoveTarget(a)} data-testid="admin-remove-btn"><Trash2 className="mr-1.5 h-3.5 w-3.5" />Remove</Button>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Card>
            )}

            <Dialog open={!!removeTarget} onOpenChange={(o) => { if (!o) { setRemoveTarget(null); setReason(""); } }}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Remove listing</DialogTitle>
                        <DialogDescription>This hides the listing from the marketplace and notifies the seller. You can restore it later.</DialogDescription>
                    </DialogHeader>
                    <p className="text-sm font-medium">{removeTarget?.title}</p>
                    <Textarea data-testid="admin-remove-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (shared with the seller)" />
                    <DialogFooter>
                        <Button variant="destructive" className="rounded-[10px]" onClick={doRemove} data-testid="admin-remove-confirm"><Trash2 className="mr-1.5 h-4 w-4" />Remove listing</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

// ----------------- Reports -----------------
function ReportsTab() {
    const [rows, setRows] = useState(null);
    const [target, setTarget] = useState(null);
    const [note, setNote] = useState("");
    const [resolution, setResolution] = useState("RESOLVED");

    const load = useCallback(() => {
        api.get("/admin/reports").then(({ data }) => setRows(data)).catch(() => { setRows([]); toast.error("Failed to load reports"); });
    }, []);
    useEffect(() => { load(); }, [load]);

    const submit = async () => {
        try {
            await api.post(`/admin/reports/${target.id}/resolve`, { status: resolution, note });
            toast.success("Report updated");
            setTarget(null); setNote(""); load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
    };

    return (
        <div className="space-y-4">
            {rows === null ? <Skeleton className="h-72 w-full rounded-xl" /> : rows.length === 0 ? (
                <EmptyState icon={Flag} title="No reports" text="The moderation queue is empty." />
            ) : (
                <Card className="overflow-hidden">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Target</TableHead>
                                <TableHead>Type</TableHead>
                                <TableHead>Reason</TableHead>
                                <TableHead>Reporter</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead className="text-right">Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {rows.map((r) => (
                                <TableRow key={r.id} data-testid="admin-report-row">
                                    <TableCell className="max-w-[200px] truncate font-medium">{r.target_label}</TableCell>
                                    <TableCell className="capitalize text-sm">{r.target_type}</TableCell>
                                    <TableCell className="max-w-[200px] truncate text-sm">{r.reason}</TableCell>
                                    <TableCell className="text-sm">{r.reporter_name || "—"}</TableCell>
                                    <TableCell><Pill value={r.status} /></TableCell>
                                    <TableCell className="text-right">
                                        {["RESOLVED", "DISMISSED"].includes(r.status) ? (
                                            <span className="text-xs text-muted-foreground">Closed</span>
                                        ) : (
                                            <Button size="sm" variant="secondary" className="rounded-[10px]" onClick={() => { setTarget(r); setResolution("RESOLVED"); }} data-testid="admin-resolve-report-btn">Review</Button>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Card>
            )}

            <Dialog open={!!target} onOpenChange={(o) => { if (!o) { setTarget(null); setNote(""); } }}>
                <DialogContent>
                    <DialogHeader><DialogTitle>Resolve report</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                        <div className="rounded-lg border border-border bg-secondary/40 p-3 text-sm">
                            <p className="font-medium">{target?.target_label}</p>
                            <p className="text-muted-foreground">{target?.reason}{target?.details ? ` — ${target.details}` : ""}</p>
                        </div>
                        <Select value={resolution} onValueChange={setResolution}>
                            <SelectTrigger data-testid="admin-report-resolution"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="RESOLVED">Resolved — action taken</SelectItem>
                                <SelectItem value="DISMISSED">Dismissed — no action</SelectItem>
                            </SelectContent>
                        </Select>
                        <Textarea data-testid="admin-report-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Internal resolution note" />
                    </div>
                    <DialogFooter><Button className="rounded-[10px]" onClick={submit} data-testid="admin-report-submit">Save resolution</Button></DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

// ----------------- Disputes -----------------
const DISPUTE_OUTCOMES = [
    { v: "REFUND_BUYER", label: "Refund buyer" },
    { v: "RELEASE_SELLER", label: "Release to seller" },
    { v: "DISMISSED", label: "Dismiss dispute" },
];

function DisputesTab() {
    const [rows, setRows] = useState(null);
    const [target, setTarget] = useState(null);
    const [outcome, setOutcome] = useState("RELEASE_SELLER");
    const [message, setMessage] = useState("");

    const load = useCallback(() => {
        api.get("/admin/disputes").then(({ data }) => setRows(data)).catch(() => { setRows([]); toast.error("Failed to load disputes"); });
    }, []);
    useEffect(() => { load(); }, [load]);

    const submit = async () => {
        try {
            await api.post(`/admin/disputes/${target.id}/resolve`, { outcome, message });
            toast.success("Dispute resolved");
            setTarget(null); setMessage(""); load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
    };

    return (
        <div className="space-y-4">
            {rows === null ? <Skeleton className="h-72 w-full rounded-xl" /> : rows.length === 0 ? (
                <EmptyState icon={Scale} title="No disputes" text="There are no open or resolved disputes." />
            ) : (
                <Card className="overflow-hidden">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Ref</TableHead>
                                <TableHead>Item</TableHead>
                                <TableHead>Opened by</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead>Outcome</TableHead>
                                <TableHead className="text-right">Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {rows.map((d) => (
                                <TableRow key={d.id} data-testid="admin-dispute-row">
                                    <TableCell className="font-mono text-xs">{d.ref}</TableCell>
                                    <TableCell className="max-w-[200px] truncate">{d.auction_title}</TableCell>
                                    <TableCell className="text-sm">{d.opener_name}</TableCell>
                                    <TableCell><Pill value={d.status} /></TableCell>
                                    <TableCell>{d.outcome ? <Pill value={d.outcome} /> : <span className="text-xs text-muted-foreground">—</span>}</TableCell>
                                    <TableCell className="text-right">
                                        {d.status === "RESOLVED" ? (
                                            <span className="text-xs text-muted-foreground">Closed</span>
                                        ) : (
                                            <Button size="sm" variant="secondary" className="rounded-[10px]" onClick={() => { setTarget(d); setOutcome("RELEASE_SELLER"); }} data-testid="admin-resolve-dispute-btn">Resolve</Button>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Card>
            )}

            <Dialog open={!!target} onOpenChange={(o) => { if (!o) { setTarget(null); setMessage(""); } }}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Resolve dispute {target?.ref}</DialogTitle>
                        <DialogDescription>Choose an outcome. Both parties are notified and any related payout is updated.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div className="max-h-40 space-y-2 overflow-y-auto rounded-lg border border-border bg-secondary/40 p-3">
                            {(target?.messages || []).map((m) => (
                                <div key={m.id} className="text-sm">
                                    <span className={cls("text-[11px]", m.is_admin ? "text-electric" : "text-muted-foreground")}>{m.author_name}</span>
                                    <p>{m.message}</p>
                                </div>
                            ))}
                        </div>
                        <Select value={outcome} onValueChange={setOutcome}>
                            <SelectTrigger data-testid="admin-dispute-outcome"><SelectValue /></SelectTrigger>
                            <SelectContent>{DISPUTE_OUTCOMES.map((o) => <SelectItem key={o.v} value={o.v}>{o.label}</SelectItem>)}</SelectContent>
                        </Select>
                        <Textarea data-testid="admin-dispute-message" value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Message to both parties (optional)" />
                    </div>
                    <DialogFooter><Button className="rounded-[10px]" onClick={submit} data-testid="admin-dispute-submit"><ShieldCheck className="mr-1.5 h-4 w-4" />Resolve</Button></DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

// ----------------- Payouts -----------------
function PayoutsTab() {
    const [rows, setRows] = useState(null);
    const load = useCallback(() => {
        api.get("/admin/payouts").then(({ data }) => setRows(data)).catch(() => { setRows([]); toast.error("Failed to load payouts"); });
    }, []);
    useEffect(() => { load(); }, [load]);

    const markPaid = async (p) => {
        try {
            await api.post(`/admin/payouts/${p.id}/mark-paid`);
            toast.success("Payout marked paid");
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
    };

    return (
        <div className="space-y-4">
            {rows === null ? <Skeleton className="h-72 w-full rounded-xl" /> : rows.length === 0 ? (
                <EmptyState icon={Banknote} title="No payouts yet" text="Seller payouts appear here after buyers complete payment." />
            ) : (
                <Card className="overflow-hidden">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Seller</TableHead>
                                <TableHead>Item</TableHead>
                                <TableHead className="text-right">Gross</TableHead>
                                <TableHead className="text-right">Fee</TableHead>
                                <TableHead className="text-right">Net</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead className="text-right">Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {rows.map((p) => (
                                <TableRow key={p.id} data-testid="admin-payout-row">
                                    <TableCell className="text-sm">{p.seller_name}</TableCell>
                                    <TableCell className="max-w-[200px] truncate">{p.auction_title}</TableCell>
                                    <TableCell className="text-right tabular-nums">{money(p.gross)}</TableCell>
                                    <TableCell className="text-right tabular-nums text-muted-foreground">{money(p.fee)}</TableCell>
                                    <TableCell className="text-right font-semibold tabular-nums text-brass">{money(p.net)}</TableCell>
                                    <TableCell><Pill value={p.status} /></TableCell>
                                    <TableCell className="text-right">
                                        {["PENDING", "PROCESSING", "READY"].includes(p.status) ? (
                                            <Button size="sm" className="rounded-[10px]" onClick={() => markPaid(p)} data-testid="admin-markpaid-btn"><CheckCircle2 className="mr-1.5 h-3.5 w-3.5" />Mark paid</Button>
                                        ) : <span className="text-xs text-muted-foreground">—</span>}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Card>
            )}
        </div>
    );
}

// ----------------- Orders -----------------
function fmtDateTime(at) {
    try { return new Date(at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }); }
    catch { return "—"; }
}

function OrdersTab() {
    const [rows, setRows] = useState(null);
    const [q, setQ] = useState("");
    const load = useCallback((query = "") => {
        api.get("/admin/orders", { params: query ? { q: query } : {} })
            .then(({ data }) => setRows(data))
            .catch(() => { setRows([]); toast.error("Failed to load orders"); });
    }, []);
    useEffect(() => { load(); }, [load]);

    return (
        <div className="space-y-4">
            <form onSubmit={(e) => { e.preventDefault(); load(q); }} className="flex gap-2">
                <div className="relative flex-1 max-w-sm">
                    <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input data-testid="admin-order-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by item" className="pl-9" />
                </div>
                <Button type="submit" variant="secondary" className="rounded-[10px]" data-testid="admin-order-search-btn">Search</Button>
            </form>
            {rows === null ? <Skeleton className="h-72 w-full rounded-xl" /> : rows.length === 0 ? (
                <EmptyState icon={ShoppingBag} title="No orders yet" text="Paid orders appear here after buyers complete checkout." />
            ) : (
                <Card className="overflow-hidden">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Item</TableHead>
                                <TableHead>Seller</TableHead>
                                <TableHead>Buyer</TableHead>
                                <TableHead className="text-right">Amount</TableHead>
                                <TableHead>Shipping</TableHead>
                                <TableHead>Return</TableHead>
                                <TableHead>Payout</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {rows.map((o) => (
                                <TableRow key={o.auction_id} data-testid="admin-order-row">
                                    <TableCell className="max-w-[240px]">
                                        <div className="flex items-center gap-2">
                                            {o.image && <img src={o.image} alt="" className="h-8 w-8 shrink-0 rounded object-cover" />}
                                            <span className="truncate font-medium">{o.title}</span>
                                        </div>
                                    </TableCell>
                                    <TableCell className="text-sm">{o.seller_name}</TableCell>
                                    <TableCell className="text-sm">{o.buyer_name}</TableCell>
                                    <TableCell className="text-right font-semibold tabular-nums text-brass">{money(o.amount)}</TableCell>
                                    <TableCell><Pill value={o.shipping_status} /></TableCell>
                                    <TableCell>{o.return_status ? <Pill value={o.return_status} /> : <span className="text-xs text-muted-foreground">—</span>}</TableCell>
                                    <TableCell>{o.payout_status ? <Pill value={o.payout_status} /> : <span className="text-xs text-muted-foreground">—</span>}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Card>
            )}
        </div>
    );
}

// ----------------- Bids -----------------
function BidsTab() {
    const [rows, setRows] = useState(null);
    const [q, setQ] = useState("");
    const load = useCallback((query = "") => {
        api.get("/admin/bids", { params: query ? { q: query } : {} })
            .then(({ data }) => setRows(data))
            .catch(() => { setRows([]); toast.error("Failed to load bids"); });
    }, []);
    useEffect(() => { load(); }, [load]);

    return (
        <div className="space-y-4">
            <form onSubmit={(e) => { e.preventDefault(); load(q); }} className="flex gap-2">
                <div className="relative flex-1 max-w-sm">
                    <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input data-testid="admin-bid-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by bidder name" className="pl-9" />
                </div>
                <Button type="submit" variant="secondary" className="rounded-[10px]" data-testid="admin-bid-search-btn">Search</Button>
            </form>
            {rows === null ? <Skeleton className="h-72 w-full rounded-xl" /> : rows.length === 0 ? (
                <EmptyState icon={Lock} title="No bids found" text="Sealed bids will appear here as buyers participate." />
            ) : (
                <Card className="overflow-hidden">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Bidder</TableHead>
                                <TableHead>Listing</TableHead>
                                <TableHead className="text-right">Sealed amount</TableHead>
                                <TableHead>Result</TableHead>
                                <TableHead>Placed</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {rows.map((b) => (
                                <TableRow key={b.id} data-testid="admin-bid-row">
                                    <TableCell className="font-medium">{b.user_name}</TableCell>
                                    <TableCell className="max-w-[240px] truncate text-sm">{b.auction_title}</TableCell>
                                    <TableCell className="text-right font-semibold tabular-nums">{money(b.amount)}</TableCell>
                                    <TableCell>
                                        {b.is_winner
                                            ? <span className="inline-flex items-center gap-1 rounded-full border border-success/30 bg-success/10 px-2 py-0.5 text-[11px] font-medium text-success"><Crown className="h-3 w-3" />Winner</span>
                                            : <span className="text-xs text-muted-foreground">—</span>}
                                    </TableCell>
                                    <TableCell className="text-xs text-muted-foreground">{fmtDateTime(b.created_at)}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Card>
            )}
        </div>
    );
}

// ----------------- Site activity -----------------
const ACTIVITY_META = {
    user: { icon: UserPlus, tone: "bg-electric/15 text-electric" },
    bid: { icon: Lock, tone: "bg-brass/15 text-brass" },
    listing: { icon: Gavel, tone: "bg-accent text-accent-foreground" },
    order: { icon: Trophy, tone: "bg-success/15 text-success" },
    dispute: { icon: Scale, tone: "bg-warning/15 text-warning" },
};

function ActivityTab() {
    const [rows, setRows] = useState(null);
    const load = useCallback(() => {
        api.get("/admin/activity").then(({ data }) => setRows(data)).catch(() => { setRows([]); toast.error("Failed to load activity"); });
    }, []);
    useEffect(() => { load(); }, [load]);

    if (rows === null) return <Skeleton className="h-72 w-full rounded-xl" />;
    if (rows.length === 0) return <EmptyState icon={ActivityIcon} title="No recent activity" text="Site events will appear here as users interact." />;

    return (
        <Card className="p-5">
            <div className="mb-4 flex items-center justify-between">
                <p className="font-display font-semibold">Recent site activity</p>
                <Button size="sm" variant="ghost" className="rounded-[10px]" onClick={load} data-testid="admin-activity-refresh"><RotateCcw className="mr-1.5 h-3.5 w-3.5" />Refresh</Button>
            </div>
            <ol className="space-y-1">
                {rows.map((e, i) => {
                    const meta = ACTIVITY_META[e.type] || ACTIVITY_META.listing;
                    const Icon = meta.icon;
                    return (
                        <li key={`${e.at}-${i}`} className="flex items-center gap-3 rounded-lg px-2 py-2 hover:bg-secondary/40" data-testid="admin-activity-row">
                            <span className={cls("flex h-8 w-8 shrink-0 items-center justify-center rounded-full", meta.tone)}><Icon className="h-4 w-4" /></span>
                            <span className="flex-1 text-sm">{e.label}</span>
                            <span className="flex shrink-0 items-center gap-1 text-xs text-muted-foreground"><Clock className="h-3 w-3" />{fmtDateTime(e.at)}</span>
                        </li>
                    );
                })}
            </ol>
        </Card>
    );
}

function EmptyState({ icon: Icon, title, text }) {
    return (
        <div className="rounded-xl border border-dashed border-border p-16 text-center">
            <Icon className="mx-auto h-8 w-8 text-muted-foreground/50" />
            <p className="mt-3 font-display font-semibold">{title}</p>
            <p className="mt-1 text-sm text-muted-foreground">{text}</p>
        </div>
    );
}

// ----------------- Research / Competitive Analysis (admin-only) -----------------
// Curated, internal-only market intelligence. Figures are sourced (see each
// competitor's "Sources" accordion) and reflect publicly reported data as of
// the date below. Treat fee ranges as reported estimates, not contractual rates.
const RESEARCH_UPDATED = "February 2025";

const COMPETITORS = [
    {
        name: "StockX",
        tagline: "Bid/ask resale order book",
        founded: "2016",
        hq: "Detroit, USA",
        category: "Sneakers, streetwear, watches, electronics, collectibles",
        model: "Continuous bid/ask marketplace — buyers place 'Bids', sellers set 'Asks', and a sale fires when they match. Pricing is fully transparent like a stock exchange.",
        fees: "Seller: 3% payment processing + tiered transaction fee (≈7%–9% by quarterly sales level). $5 US minimum.",
        auth: "Centralized authentication hubs physically inspect every item before it ships to the buyer.",
        strengths: ["Deep liquidity & transparent market pricing", "Trusted authentication at massive scale", "Instant 'Sell Now' to the highest standing bid"],
        weaknesses: ["Open order book → bidding wars & price anchoring", "Commoditized race-to-lowest-ask for sellers", "Works best only for standardized SKUs"],
        edge: "OmniAuc's sealed single bid removes the price anchoring and undercutting StockX's open book encourages — buyers bid true value, once.",
        sources: [
            { label: "StockX seller fee schedule", url: "https://stockx.com/help/articles/what-are-stockxs-fees-for-sellers" },
            { label: "StockX seller payment processing fees", url: "https://stockx.com/news/seller-payment-processing-fees/" },
        ],
    },
    {
        name: "Bezel",
        tagline: "White-glove luxury watch marketplace",
        founded: "2022",
        hq: "Los Angeles, USA",
        category: "Luxury & pre-owned watches only",
        model: "Curated consignment-style marketplace. Every sold watch is routed to Bezel HQ for in-house authentication before it reaches the buyer.",
        fees: "Buyer: ~2.9% card processing. Seller: reported ~5.5%–10.5% commission (range, not a full table).",
        auth: "Rigorous in-house watch authentication; reportedly rejects ~24% of inspected watches (esp. Rolex).",
        strengths: ["Premium trust & authentication brand", "Category focus builds expertise", "Strong buyer protection narrative"],
        weaknesses: ["Single narrow category (watches)", "High-touch model is hard to scale", "Fixed-price listings — no auction tension"],
        edge: "OmniAuc applies a trust layer (Stripe KYC + verified buyers) across many categories, not just watches, while sealed bidding creates urgency Bezel's fixed prices lack.",
        sources: [
            { label: "Heddels — Bezel marketplace overview", url: "https://www.heddels.com/2024/11/bezel-the-trusted-marketplace-for-luxury-watches/" },
            { label: "Hodinkee — Bezel rejection rate report", url: "https://www.hodinkee.com/articles/online-watch-dealer-bezel-is-rejecting-more-than-a-quarter-of-the-watches-it-inspects-mostly-rolex" },
        ],
    },
    {
        name: "eBay Live",
        tagline: "Livestream shopping & auctions at scale",
        founded: "1995 (Live: 2022+)",
        hq: "San Jose, USA",
        category: "Trading cards, sneakers, luxury, collectibles, general",
        model: "Livestream auctions plus classic auction & Buy It Now formats, layered on eBay's enormous existing marketplace and logistics.",
        fees: "No buyer fee for authenticity program. Event example: $3 upfront submission + 5% transaction fee on sale.",
        auth: "Authenticity Guarantee — authentication on eligible watches sold for $2,000+ (US) and select categories.",
        strengths: ["Unmatched audience & built-in traffic", "Multiple selling formats in one place", "Mature logistics, payments & buyer protection"],
        weaknesses: ["Visible/open bidding — no sealed format", "Generalist UX, low category curation", "Live format favors entertainers, not fairness"],
        edge: "OmniAuc's one-bid-per-user fairness model and sealed format is a sharp, defensible niche eBay's broad open-auction engine doesn't serve.",
        sources: [
            { label: "eBay launches livestream shopping tour", url: "https://www.ebayinc.com/stories/news/ebay-launches-livestream-shopping-tour-across-the-us/" },
            { label: "eBay watch authentication ($2,000+)", url: "https://www.ebayinc.com/stories/news/ebay-launches-authentication-on-all-watches-sold-for-2-000-or-more-in-the-us-with-new-service-authenticity-guarantee/" },
        ],
    },
    {
        name: "hiBid",
        tagline: "Auctioneer software + marketplace",
        founded: "1999 (Auction Flex)",
        hq: "Lincoln, USA",
        category: "Estate, industrial, equipment, general auctions",
        model: "B2B auction software (Auction Flex / hiBid) that distributes traditional auctioneers' catalogs online with timed, webcast and absentee bidding.",
        fees: "Monthly software subscription + ~2% auction fee + ~$0.25 per unique bid (capped) + optional add-ons.",
        auth: "Relies on the individual auctioneer/seller; no centralized item authentication layer.",
        strengths: ["Entrenched with professional auctioneers", "Robust timed/webcast/absentee tooling", "Recurring software + transaction revenue"],
        weaknesses: ["Dated, utilitarian consumer UX", "Trust depends on each auctioneer", "Not built for sealed single-bid consumers"],
        edge: "OmniAuc is consumer-first with a modern sealed-bid UX and platform-level KYC trust — the opposite of hiBid's seller-managed, software-heavy model.",
        sources: [
            { label: "Auction Flex / hiBid pricing", url: "https://trial.auctionflex.com/auction-flex-360-pricing" },
            { label: "hiBid bidder registration", url: "https://help.hibid.com/en/articles/11989141-registering-to-bid-on-hibid-com" },
        ],
    },
    {
        name: "TP Auctions",
        tagline: "Luxury auction app",
        founded: "2024",
        hq: "USA",
        category: "Watches, handbags, accessories, jewelry",
        model: "App-driven luxury auction marketplace for high-value goods with timed bidding. (Note: the older Tophatter rapid-bid platform has shut down.)",
        fees: "Not publicly disclosed in detail; positioned as a premium luxury auction app.",
        auth: "Curated luxury focus; authentication approach not fully public.",
        strengths: ["Mobile-first, modern luxury positioning", "Focused high-value categories", "App engagement / push-driven bidding"],
        weaknesses: ["Early-stage, limited public track record", "Opaque fee & authentication detail", "Open bidding, not sealed"],
        edge: "OmniAuc's transparent fairness rules (one sealed bid, max-bidder or timer close) and published trust model contrast with TP's opaque early-stage offering.",
        sources: [
            { label: "TP Auctions site", url: "https://tpauctions.net" },
            { label: "TP Auctions app (App Store)", url: "https://apps.apple.com/us/app/tp-auctions/id6499424496" },
        ],
    },
];

// Compact comparison matrix. OmniAuc is the highlighted reference column.
const MATRIX_ROWS = [
    { label: "Bid format", omni: "Sealed single bid", values: ["Open bid/ask book", "Fixed price", "Open/live auction", "Timed/webcast", "Timed auction"] },
    { label: "Bidder visibility", omni: "Hidden (blind)", values: ["Fully visible", "N/A (fixed)", "Visible", "Visible", "Visible"] },
    { label: "One bid per user & IP", omni: "yes", values: ["no", "no", "no", "no", "no"] },
    { label: "Buyer verification", omni: "Phone OTP (Twilio)", values: ["Account only", "Account only", "Account only", "Account only", "Account only"] },
    { label: "Seller KYC", omni: "Stripe Connect", values: ["Onboarding", "Consignment", "Onboarding", "Auctioneer-led", "Onboarding"] },
    { label: "Authentication", omni: "Trust + dispute layer", values: ["Central hubs", "In-house (watches)", "Eligible items", "Seller-led", "Curated"] },
    { label: "Platform fee", omni: "~8% flat", values: ["~7–9%+3%", "~5.5–10.5%", "~5%+fees", "~2%+per-bid", "Undisclosed"] },
    { label: "Category focus", omni: "Multi-category", values: ["Sneakers/resale", "Watches", "Generalist", "Estate/industrial", "Luxury goods"] },
];

const TAKEAWAYS = [
    { tone: "good", title: "Own the fairness niche", text: "No major competitor uses a sealed, one-bid-per-user format. This is OmniAuc's defensible wedge — lean into 'fair, no bidding wars' messaging." },
    { tone: "good", title: "Trust layer is table-stakes", text: "StockX, Bezel and eBay all win on trust/authentication. OmniAuc's Stripe KYC + phone-verified buyers + disputes is competitive — keep surfacing the Verified badge everywhere." },
    { tone: "watch", title: "Authentication gap", text: "Competitors physically authenticate high-value items. For premium categories, consider an optional authentication partner or escrow to match buyer expectations." },
    { tone: "watch", title: "Fee transparency as a weapon", text: "Several rivals hide or stack fees (per-bid, processing tiers). A simple, published ~8% flat fee is a marketing advantage — make it loud." },
];

function CompetitorCard({ c }) {
    return (
        <Card className="flex flex-col p-5" data-testid={`research-competitor-${c.name.replace(/\s+/g, "-").toLowerCase()}`}>
            <div className="flex items-start justify-between gap-3">
                <div>
                    <h3 className="font-display text-lg font-semibold">{c.name}</h3>
                    <p className="text-xs font-medium text-electric">{c.tagline}</p>
                </div>
                <span className="rounded-md bg-secondary px-2 py-1 text-[11px] text-muted-foreground">Est. {c.founded}</span>
            </div>

            <dl className="mt-4 space-y-2 text-sm">
                <div><dt className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Category</dt><dd className="text-foreground">{c.category}</dd></div>
                <div><dt className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Model</dt><dd className="text-muted-foreground">{c.model}</dd></div>
                <div><dt className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Fees</dt><dd className="text-muted-foreground">{c.fees}</dd></div>
                <div><dt className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Authentication / trust</dt><dd className="text-muted-foreground">{c.auth}</dd></div>
            </dl>

            <div className="mt-4 grid gap-3 sm:grid-cols-2">
                <div className="rounded-lg border border-success/20 bg-success/5 p-3">
                    <p className="mb-1.5 flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide text-success"><CheckCircle2 className="h-3.5 w-3.5" />Strengths</p>
                    <ul className="space-y-1 text-xs text-muted-foreground">
                        {c.strengths.map((s) => <li key={s} className="flex gap-1.5"><span className="text-success">+</span>{s}</li>)}
                    </ul>
                </div>
                <div className="rounded-lg border border-destructive/20 bg-destructive/5 p-3">
                    <p className="mb-1.5 flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide text-destructive"><XCircle className="h-3.5 w-3.5" />Weaknesses</p>
                    <ul className="space-y-1 text-xs text-muted-foreground">
                        {c.weaknesses.map((w) => <li key={w} className="flex gap-1.5"><span className="text-destructive">−</span>{w}</li>)}
                    </ul>
                </div>
            </div>

            <div className="mt-3 rounded-lg border border-electric/20 bg-electric/5 p-3">
                <p className="mb-1 flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide text-electric"><Target className="h-3.5 w-3.5" />OmniAuc's edge</p>
                <p className="text-xs text-muted-foreground">{c.edge}</p>
            </div>

            <Accordion type="single" collapsible className="mt-2">
                <AccordionItem value="sources" className="border-b-0">
                    <AccordionTrigger className="py-2 text-xs text-muted-foreground hover:no-underline" data-testid={`research-sources-${c.name.replace(/\s+/g, "-").toLowerCase()}`}>
                        Sources ({c.sources.length})
                    </AccordionTrigger>
                    <AccordionContent>
                        <ul className="space-y-1.5">
                            {c.sources.map((s) => (
                                <li key={s.url}>
                                    <a href={s.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-electric hover:underline">
                                        <ExternalLink className="h-3 w-3" />{s.label}
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </AccordionContent>
                </AccordionItem>
            </Accordion>
        </Card>
    );
}

function MatrixCell({ value }) {
    if (value === "yes") return <span className="inline-flex items-center gap-1 text-success"><CheckCircle2 className="h-3.5 w-3.5" />Yes</span>;
    if (value === "no") return <span className="inline-flex items-center gap-1 text-muted-foreground"><Minus className="h-3.5 w-3.5" />No</span>;
    return <span className="text-muted-foreground">{value}</span>;
}

function ResearchTab() {
    return (
        <div className="space-y-6" data-testid="admin-research-tab">
            {/* Intro */}
            <Card className="p-6">
                <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="flex items-start gap-3">
                        <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-electric/10 text-electric"><Swords className="h-5 w-5" /></span>
                        <div>
                            <h2 className="font-display text-xl font-semibold tracking-tight">Competitive analysis</h2>
                            <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
                                Internal market intelligence on the five most relevant marketplaces to OmniAuc. Use it to inform pricing, trust features, and roadmap. Figures are publicly-reported estimates &mdash; verify before quoting externally.
                            </p>
                        </div>
                    </div>
                    <span className="rounded-md border border-border bg-secondary px-2.5 py-1 text-[11px] text-muted-foreground">Updated {RESEARCH_UPDATED}</span>
                </div>
            </Card>

            {/* Strategic takeaways */}
            <div>
                <h3 className="mb-3 flex items-center gap-2 font-display font-semibold"><Lightbulb className="h-4 w-4 text-brass" />Strategic takeaways</h3>
                <div className="grid gap-4 sm:grid-cols-2">
                    {TAKEAWAYS.map((t) => (
                        <Card key={t.title} className={cls("p-4", t.tone === "good" ? "border-success/30" : "border-warning/30")} data-testid="research-takeaway">
                            <p className={cls("flex items-center gap-1.5 text-sm font-semibold", t.tone === "good" ? "text-success" : "text-warning")}>
                                {t.tone === "good" ? <Sparkles className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}{t.title}
                            </p>
                            <p className="mt-1 text-sm text-muted-foreground">{t.text}</p>
                        </Card>
                    ))}
                </div>
            </div>

            {/* Comparison matrix */}
            <div>
                <h3 className="mb-3 flex items-center gap-2 font-display font-semibold"><BarChart3 className="h-4 w-4 text-electric" />Side-by-side matrix</h3>
                <Card className="overflow-x-auto p-0">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead className="min-w-[140px]">Dimension</TableHead>
                                <TableHead className="min-w-[150px] bg-electric/10 font-semibold text-electric">OmniAuc</TableHead>
                                {COMPETITORS.map((c) => <TableHead key={c.name} className="min-w-[130px]">{c.name}</TableHead>)}
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {MATRIX_ROWS.map((row) => (
                                <TableRow key={row.label} data-testid="research-matrix-row">
                                    <TableCell className="font-medium">{row.label}</TableCell>
                                    <TableCell className="bg-electric/5 text-sm font-medium text-foreground"><MatrixCell value={row.omni} /></TableCell>
                                    {row.values.map((v, i) => <TableCell key={i} className="text-sm"><MatrixCell value={v} /></TableCell>)}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Card>
            </div>

            {/* Competitor deep-dives */}
            <div>
                <h3 className="mb-3 flex items-center gap-2 font-display font-semibold"><Search className="h-4 w-4 text-electric" />Competitor deep-dives</h3>
                <div className="grid gap-5 lg:grid-cols-2">
                    {COMPETITORS.map((c) => <CompetitorCard key={c.name} c={c} />)}
                </div>
            </div>

            <p className="text-xs text-muted-foreground">
                This page is internal and admin-only. Fee ranges and authentication details are summarized from public sources (linked per competitor) and may change; confirm current terms before relying on them in external materials.
            </p>
        </div>
    );
}

// ----------------- Page -----------------
export default function AdminDashboard() {
    const { user, loading, config } = useAuth();
    const navigate = useNavigate();
    const [stats, setStats] = useState(null);

    useEffect(() => {
        if (loading) return;
        if (!user) { navigate("/login"); return; }
        if (!user.is_admin) { navigate("/"); }
    }, [loading, user, navigate]);

    useEffect(() => {
        if (user?.is_admin) {
            api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => setStats(null));
        }
    }, [user]);

    if (loading || !user) return null;
    if (!user.is_admin) return null;

    return (
        <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6">
            <div className="flex items-center gap-2">
                <ShieldCheck className="h-6 w-6 text-electric" />
                <h1 className="font-display text-3xl font-semibold tracking-tight">Admin console</h1>
            </div>
            <p className="text-sm text-muted-foreground">Platform moderation, user & listing management, dispute resolution, and payouts.</p>

            {config?.stripe_connect_real ? (
                <div className="mt-4 flex items-center gap-2 rounded-lg border border-success/30 bg-success/10 px-3 py-2 text-xs text-success" data-testid="admin-payments-live-banner">
                    <CheckCircle2 className="h-4 w-4 shrink-0" />
                    Live mode active — payments, payouts and seller verification are processed through Stripe.
                </div>
            ) : (
                <div className="mt-4 flex items-center gap-2 rounded-lg border border-warning/30 bg-warning/10 px-3 py-2 text-xs text-warning" data-testid="admin-payments-sim-banner">
                    <AlertTriangle className="h-4 w-4 shrink-0" />
                    Payments, payouts and seller verification run in simulated mode until live API keys are configured.
                </div>
            )}

            <Tabs defaultValue="overview" className="mt-6">
                <TabsList className="flex-wrap">
                    <TabsTrigger value="overview" data-testid="admin-tab-overview"><TrendingUp className="mr-1.5 h-4 w-4" />Overview</TabsTrigger>
                    <TabsTrigger value="users" data-testid="admin-tab-users"><UsersIcon className="mr-1.5 h-4 w-4" />Users</TabsTrigger>
                    <TabsTrigger value="listings" data-testid="admin-tab-listings"><Gavel className="mr-1.5 h-4 w-4" />Listings</TabsTrigger>
                    <TabsTrigger value="bids" data-testid="admin-tab-bids"><Lock className="mr-1.5 h-4 w-4" />Bids</TabsTrigger>
                    <TabsTrigger value="orders" data-testid="admin-tab-orders"><Package className="mr-1.5 h-4 w-4" />Orders</TabsTrigger>
                    <TabsTrigger value="reports" data-testid="admin-tab-reports"><Flag className="mr-1.5 h-4 w-4" />Reports</TabsTrigger>
                    <TabsTrigger value="disputes" data-testid="admin-tab-disputes"><Scale className="mr-1.5 h-4 w-4" />Disputes</TabsTrigger>
                    <TabsTrigger value="payouts" data-testid="admin-tab-payouts"><Banknote className="mr-1.5 h-4 w-4" />Payouts</TabsTrigger>
                    <TabsTrigger value="activity" data-testid="admin-tab-activity"><ActivityIcon className="mr-1.5 h-4 w-4" />Activity</TabsTrigger>
                    <TabsTrigger value="research" data-testid="admin-tab-research"><Swords className="mr-1.5 h-4 w-4" />Research</TabsTrigger>
                </TabsList>

                <TabsContent value="overview" className="mt-6"><Overview stats={stats} /></TabsContent>
                <TabsContent value="users" className="mt-6"><UsersTab /></TabsContent>
                <TabsContent value="listings" className="mt-6"><ListingsTab /></TabsContent>
                <TabsContent value="bids" className="mt-6"><BidsTab /></TabsContent>
                <TabsContent value="orders" className="mt-6"><OrdersTab /></TabsContent>
                <TabsContent value="reports" className="mt-6"><ReportsTab /></TabsContent>
                <TabsContent value="disputes" className="mt-6"><DisputesTab /></TabsContent>
                <TabsContent value="payouts" className="mt-6"><PayoutsTab /></TabsContent>
                <TabsContent value="activity" className="mt-6"><ActivityTab /></TabsContent>
                <TabsContent value="research" className="mt-6"><ResearchTab /></TabsContent>
            </Tabs>
        </div>
    );
}
